

































































































import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { ObserveVisibility } from 'vue-observe-visibility';
import debounce from 'debounce';
import { EditorBlockView } from '@getaccept/editor-view';
import type { EditorContent, MergeValues } from '@getaccept/editor-lib';
import type { Recipient } from '@getaccept/lib-shared/src/types/Recipient';
import { DocumentPageType } from '@getaccept/lib-shared/src/enums/document-page-type';
import type { DocumentPage } from '@getaccept/lib-shared/src/types/document-page';
import PageSkeletonLoader from '@getaccept/lib-shared/src/skeleton-loader/components/PageSkeletonLoader.vue';
import type { EditorRecipientInput } from '@getaccept/lib-shared/src/types/editor-recipient-input';
import type { SigningTheme } from '@getaccept/lib-shared/src/signing-theme/types/theme';
import DocumentContentPage from '@getaccept/lib-shared/src/components/DocumentContentPage.vue';
import type { SigningPageUser } from '@getaccept/lib-shared/src/types/signing-page-user';
import ContentLinkPage from '@getaccept/lib-shared/src/components/ContentLinkPage.vue';
import VideoPage from '@getaccept/lib-shared/src/components/VideoPage.vue';
import type { PartiallySignedEvent } from '@getaccept/editor-lib/src/types/signed-field';
import type { Comment } from '@getaccept/lib-shared/src/contextual-comments/types/comment';

@Component({
  components: {
    VideoPage,
    ContentLinkPage,
    DocumentContentPage,
    PageSkeletonLoader,
    EditorBlockView,
  },
  directives: {
    'observe-visibility': ObserveVisibility,
  },
  data: () => ({
    DocumentPageType,
  }),
})
export default class ContentBlockContainer extends Vue {
  @Prop() authId: string;
  @Prop() token: { jwt: string; exp: number };
  @Prop() documentId: string;
  @Prop() page!: DocumentPage;
  @Prop() pageWidth: number;
  @Prop() recipient: Recipient;
  @Prop() allRecipients: Recipient[];
  @Prop() sender: SigningPageUser;
  @Prop() mergeValues: MergeValues;
  @Prop() theme: SigningTheme;
  @Prop() focusedRequiredId?: string;
  @Prop({ default: false }) hideFields!: boolean;
  @Prop() isPreview!: boolean;
  @Prop() isSigned!: boolean;
  @Prop() requiredStepsLeft!: boolean;
  @Prop() initials: string;
  @Prop() verifiedInitialFieldIds: string[];
  @Prop() editorRecipientInput: EditorRecipientInput[];
  @Prop() partiallySignedFields: PartiallySignedEvent[];
  @Prop({ required: true }) comments: Comment[];
  @Prop() selectedCommentId: string;
  @Prop({ default: false }) hasContextualCommenting: boolean;

  isVisible = false;
  pageTooltip = false;
  isImageLoaded: boolean = this.evaluateImageLoaded;

  @Watch('isVisible')
  showPageTooltip() {
    if (this.isVisible) {
      this.pageTooltip = true;
      setTimeout(() => {
        this.pageTooltip = false;
      }, 2000);
    }
  }

  filterComments(comments: Comment[], editorBlockId: string): Comment[] {
    return comments.filter(comment => comment.editorSelection.editorBlockId === editorBlockId);
  }

  handleUpdateField({ fieldId, value }: { fieldId: string; value: string }) {
    this.$emit('updateField', { fieldId, value });
  }

  handleClickLinkField(id: string) {
    this.$emit('clickLinkField', id);
  }
  handleClickInitialsField(id: string) {
    this.$emit('clickInitialsField', id);
  }

  handleClickSignField() {
    this.$emit('clickSignField');
  }

  debounceHandleVisibility: () => void = debounce(this.handleVisibility, 200, true);

  handleVisibility(isVisible: boolean) {
    if (isVisible && this.shouldFetchThumbUrl) {
      this.$emit('loadPageThumbUrl', this.page.id);
    }
    this.isVisible = isVisible;
  }

  get shouldFetchThumbUrl(): boolean {
    return (
      this.page.pageType === DocumentPageType.DocumentContent && this.page.id && !this.page.thumbUrl
    );
  }

  handleTrackVideo(payload: { id: string; viewDuration: number }) {
    this.$emit('trackVideo', payload);
  }

  handleImageIsLoaded() {
    this.isImageLoaded = true;
    this.$nextTick(() => this.$emit('imageLoaded'));
  }

  handleContentLinkClick(id: string) {
    this.$emit('contentLinkClick', id);
  }

  get skeletonHeight(): number {
    return this.page ? (this.pageWidth / this.page.pageWidth) * this.page.pageHeight : 900;
  }

  get editorBlockId(): string {
    return this.page?.editorBlock?.id;
  }

  get editorContent(): EditorContent {
    return this.page?.editorBlock?.content;
  }

  get evaluateImageLoaded(): boolean {
    return [DocumentPageType.ContentLink, DocumentPageType.Video, DocumentPageType.Editor].includes(
      this.page.pageType
    );
  }
}
